// src/pages/AssetDetailsView.jsx
// Asset Details (AUF-styled) – read-only → edit toggle controlled by Navbar
// Mirrors PublishedFormPage’s pattern: PreviewFields (no submitRow) + separate SubmitButtonComponent.
// Fetches: assets/{assetId}, organizations/{orgId}.assetUploadFormId, forms/{aufId} (decompress blobs).
// Hydrates answers from the asset’s latest version snapshot (fallback to metadata.responses).
// Edit mode: toggled by Navbar via window event "assetdetails:set-edit"; page broadcasts "assetdetails:status".

import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import {
  Box,
  Typography,
  Divider,
  Alert,
  Snackbar,
  Paper,
} from '@mui/material';

import { useAuth } from '../contexts/AuthContext';
import { useMultiAnswerField } from '../contexts/MultiAnswerFieldContext';
import { applyTransformations } from '../utils/transformationUtils';

// Minimal runtime providers to render the AUF surface on this page
// ⚠️ IMPORTANT: DO NOT create a new FormProvider here — the route already wraps this page in one.
import { FormThemeProvider, useFormTheme } from '../contexts/FormThemeContext';
import { FormLayoutProvider, useFormLayout } from '../contexts/FormLayoutContext';
import { FieldMetadataProvider, useFieldMetadata } from '../contexts/FieldMetadataContext';
import { FieldEditProvider } from '../contexts/FieldEditContext';
import { MultiAnswerFieldProvider } from '../contexts/MultiAnswerFieldContext';
import { DropdownDependenciesProvider } from '../contexts/DropdownDependenciesContext';
import { FormSubmissionSettingsProvider } from '../contexts/FormSubmissionSettingsContext';
import { FormActionsProvider } from '../contexts/FormActionsContext';
import { FormSubmissionsProvider, useFormSubmissions } from '../contexts/FormSubmissionsContext';
import { FormAnswersProvider, useFormAnswers } from '../contexts/FormAnswersContext';

import PreviewFields from '../components/FormSettings/PreviewFields';
import SubmitButtonComponent from '../components/FormSettings/FormDesignLayout/SubmitButtonComponent';

import { decompressData } from '../utils/decompressionUtils';

// Versions side drawer (version history, quick actions)
import VersionsDrawer from '../AssetManager/VersionsDrawer';

// bring in setters so we can stamp AUF formId into *the existing* FormContext (provided by the route)
import { useFormContext } from '../contexts/FormContext';

/* ──────────────────────────────────────────────────────────────
   Helpers
   ────────────────────────────────────────────────────────────── */

/** Normalize { ufi: { value } } → { ufi: value } with label_* keys preserved */
function normalizeResponsesMap(r) {
  if (!r || typeof r !== 'object') return {};
  const out = {};
  for (const [k, v] of Object.entries(r)) {
    if (k.startsWith('label_')) {
      out[k] = v;
      continue;
    }
    out[k] = (v && typeof v === 'object' && 'value' in v) ? v.value : v;
  }
  return out;
}

/** Hydrate answers ONLY after context fieldsMetadata has UFIs.
 *  Fix: for dropdowns, map archived labels back to optionId(s) so controls render selections.
 *  Idempotent: skip setAnswer when the current value is already equal.
 */
function AnswersHydrator({ answers }) {
  const { setAnswer, answersByUfi } = useFormAnswers() || {};
  const { fieldsMetadata } = useFieldMetadata() || {};
  const { dropdownOptions } = useMultiAnswerField() || {};

  const isSameValue = React.useCallback((a, b) => {
    if (a === b) return true;
    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
      return true;
    }
    if (a && b && typeof a === 'object' && typeof b === 'object') {
      const aId = a.assetId ?? a.id ?? null;
      const bId = b.assetId ?? b.id ?? null;
      const aV  = a.versionNumber ?? null;
      const bV  = b.versionNumber ?? null;
      if (aId != null || bId != null || aV != null || bV != null) {
        return aId === bId && aV === bV;
      }
      const aKeys = Object.keys(a);
      const bKeys = Object.keys(b);
      if (aKeys.length !== bKeys.length) return false;
      for (const k of aKeys) if (a[k] !== b[k]) return false;
      return true;
    }
    return false;
  }, []);

  const isDropdown = (t) => t === 'single-select' || t === 'multi-select';

  // Robust label → optionId: exact match, then transformed label match, then case/trim match
  const mapLabelToOptionId = React.useCallback((label, meta) => {
    if (label == null || label === '') return label;
    if (!meta) return label;

    const setUfi = meta.optionsSetUFI;
    const opts = (setUfi && dropdownOptions?.[setUfi]) || [];
    if (!opts.length) return label;

    // 1) exact label
    let opt = opts.find((o) => o.label === label);
    if (opt) return opt.optionId;

    // 2) transformed label (the submit pipeline may have applied transformations)
    try {
      const transformed = (o) => applyTransformations(o.label, meta.transformations);
      opt = opts.find((o) => transformed(o) === label);
      if (opt) return opt.optionId;
    } catch (_) {
      /* ignore */
    }

    // 3) relaxed: trim + case-insensitive match
    const L = String(label).trim().toLowerCase();
    opt = opts.find((o) => String(o.label).trim().toLowerCase() === L);
    return opt ? opt.optionId : label; // fall back to original when no match
  }, [dropdownOptions]);

  const mapArrayLabels = React.useCallback((arr, meta) => {
    if (!Array.isArray(arr)) return arr;
    return arr.map((lbl) => mapLabelToOptionId(lbl, meta));
  }, [mapLabelToOptionId]);

  React.useEffect(() => {
    if (!answers || !fieldsMetadata || typeof fieldsMetadata !== 'object') return;
    if (typeof setAnswer !== 'function') return;

    try {
      const validUfis = new Set(
        Object.values(fieldsMetadata || {}).map((m) => m?.ufi).filter(Boolean)
      );

      // eslint-disable-next-line no-console
      console.log('[AssetDetailsView] AnswersHydrator maps:', {
        answersKeys: Object.keys(answers || {}),
        fieldCount: Object.keys(fieldsMetadata || {}).length,
        validUfisCount: validUfis.size,
      });

      Object.entries(answers).forEach(([key, rawValue]) => {
        if (!key || key.startsWith('label_')) return;

        // Resolve meta + ufi by UFI or fieldId
        let meta = null;
        let ufi = null;

        if (validUfis.has(key)) {
          ufi = key;
          meta = Object.values(fieldsMetadata || {}).find((m) => m?.ufi === ufi) || null;
        } else if (fieldsMetadata[key]?.ufi) {
          ufi = fieldsMetadata[key].ufi;
          if (!validUfis.has(ufi)) {
            console.warn('[AssetDetailsView] Skipping answer (no matching UFI in context yet):', key);
            return;
          }
          meta = fieldsMetadata[key];
        } else {
          console.warn('[AssetDetailsView] Skipping answer (unknown key):', key);
          return;
        }

        let value = rawValue;

        // Convert archived labels back to optionId(s) for dropdowns
        if (meta && isDropdown(meta.type)) {
          if (Array.isArray(value)) {
            value = mapArrayLabels(value, meta);
          } else if (typeof value === 'string') {
            value = mapLabelToOptionId(value, meta);
          }
        }

        const current = answersByUfi?.[ufi];
        if (!isSameValue(current, value)) {
          try { setAnswer(ufi, value); } catch (_) {}
        }
      });
    } catch (e) {
      console.warn('[AssetDetailsView] AnswersHydrator failed:', e?.message || e);
    }
  }, [answers, fieldsMetadata, answersByUfi, setAnswer, isSameValue, mapLabelToOptionId, mapArrayLabels]);

  return null;
}

/* ──────────────────────────────────────────────────────────────
   Page
   ────────────────────────────────────────────────────────────── */
export default function AssetDetailsView() {
  const { assetId } = useParams();
  const { organizationId, hasPermission } = useAuth();

  const [asset, setAsset] = useState(null);
  const [aufId, setAufId] = useState(null);
  const [fields, setFields] = useState(null);
  const [layout, setLayout] = useState(null);
  const [options, setOptions] = useState(null);
  const [theme, setTheme] = useState(null);

  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState(null);

  // Navbar-driven edit state
  const [isEditing, setIsEditing] = useState(false);

  // Toast feedback (parity with PublishedFormPage)
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  const [toastSeverity, setToastSeverity] = useState('info');
  const showToast = (msg, severity = 'info') => {
    setToastMsg(msg);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  /* ─────────────────────────────────────────────────────────────
     Versions drawer state + handlers
     - Open when AttachFieldComponent dispatches 'asset:open-versions'
     - Selecting a version replaces the in-memory asset (preview updates)
     - "+ New version" tells the attach field to open its picker
     ──────────────────────────────────────────────────────────── */
  const [versionsOpen, setVersionsOpen] = useState(false);

  // Listen for field-level "open versions" requests
  useEffect(() => {
    const onOpen = (e) => {
      // If the event specifies an assetId and it differs, you can
      // opt to ignore or re-fetch that other asset. For now, just open.
      setVersionsOpen(true);
    };
    window.addEventListener('asset:open-versions', onOpen);
    return () => window.removeEventListener('asset:open-versions', onOpen);
  }, []);

  // When a row in the drawer is clicked, show that version immediately
  const handleSelectVersion = React.useCallback((row) => {
    if (!row) return;
    // Replace the previewed asset; answers useMemo will rebuild from it
    setAsset({ id: row.id, ...row });
    setVersionsOpen(false);
  }, []);

  // "+ New version" → reuses AUF field’s one-shot picker event
  const handleAddNewVersion = React.useCallback(() => {
    try { window.dispatchEvent(new Event('auf:open-file-picker')); } catch {}
    setVersionsOpen(false);
  }, []);

  //  setters from FormContext so we can stamp AUF id as the active form (coming from the OUTER provider)
  const { setFormId, setFormDetails } = useFormContext() || {};

  /* ─────────────────────────────────────────────────────────────
     Load asset + AUF blobs (single-shot)
     ──────────────────────────────────────────────────────────── */
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        if (!assetId || !organizationId) {
          throw new Error('Missing assetId or organizationId.');
        }

        // 1) Asset
        const aSnap = await getDoc(doc(db, 'assets', String(assetId)));
        if (!aSnap.exists()) throw new Error('Asset not found.');
        const a = { id: aSnap.id, ...aSnap.data() };

        // Client-side org guard for better UX (writes are server-guarded)
        if (a.organizationId !== organizationId) {
          throw new Error('You do not have access to this asset.');
        }

        // 2) AUF pointer from org
        const orgSnap = await getDoc(doc(db, 'organizations', organizationId));
        const org = orgSnap.exists() ? orgSnap.data() : {};
        const auf = org.assetUploadFormId || null;

        if (!auf) {
          // No AUF published – we still render a simple summary; skip AUF blobs
          if (!cancelled) {
            setAsset(a);
            setAufId(null);
            setFields(null);
            setLayout(null);
            setOptions(null);
            setTheme(null);
            setLoading(false);
          }
          return;
        }

        // 3) AUF blobs (compressed base64 → decompress to plain objects)
        const fSnap = await getDoc(doc(db, 'forms', String(auf)));
        if (!fSnap.exists()) throw new Error('AUF form doc not found for this org.');
        const fd = fSnap.data() || {};

        const fieldsMeta = fd.fieldsMetadata ? await decompressData(fd.fieldsMetadata) : {};
        const layoutCfg  = fd.layoutConfig   ? await decompressData(fd.layoutConfig)   : {};
        const ddOptions  = fd.dropdownOptions? await decompressData(fd.dropdownOptions): {};
        const themeBlob  = fd.theme          ? await decompressData(fd.theme)          : {};

        if (cancelled) return;

        setAsset(a);
        setAufId(auf);
        setFields(fieldsMeta);
        setLayout(layoutCfg);
        setOptions(ddOptions);
        setTheme(themeBlob);
        setLoading(false);
      } catch (e) {
        if (!cancelled) {
          setErrorMsg(e?.message || String(e));
          setLoading(false);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [assetId, organizationId]);

  // 🆕 Stamp the AUF formId into the global (outer) FormContext so all consumer
  // providers (Theme/Layout/Fields/etc.) have a formId and stop “formId not ready” logs.
  // We keep it read-only; we’re not trying to save anything here.
  useEffect(() => {
    if (!aufId || typeof setFormId !== 'function') return;
    try {
      setFormId(aufId);
      if (typeof setFormDetails === 'function') {
        // Keep this minimal; we only hint that this surface is AUF
        setFormDetails((prev) => ({
          ...(prev || {}),
          isAssetUploadForm: true,
        }));
      }
      // eslint-disable-next-line no-console
      console.log('[AssetDetailsView] setFormId(aufId) →', aufId);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[AssetDetailsView] setFormId failed:', e?.message || e);
    }
  }, [aufId, setFormId, setFormDetails]);

  /* ─────────────────────────────────────────────────────────────
     Build a normalized metadata map that guarantees every non-submit
     field in the layout has a metadata object (prevents undefined crashes
     inside FieldRenderer when it reads fieldMetadata.label, etc.)
     ──────────────────────────────────────────────────────────── */
  const normalizedFields = useMemo(() => {
    if (!layout || typeof layout !== 'object') return fields || {};
    const out = { ...(fields || {}) };

    const allRows = Object.keys(layout)
      .filter(k => Array.isArray(layout[k]))
      .reduce((acc, k) => acc.concat(layout[k]), []);

    allRows.forEach(cell => {
      if (!cell || cell.type === 'submit') return;
      const fid = cell.id;
      if (!fid) return;

      if (!out[fid]) {
        out[fid] = {
          id: fid,
          ufi: cell.ufi || fid,
          type: cell.type || 'text',
          label: cell.label || cell.name || fid,
          required: !!cell.required,
          defaultValue: cell.defaultValue ?? (cell.type === 'attach' ? null : ''),
          style: cell.style || {},
          // minimal props to keep FieldRenderer happy
        };
        // eslint-disable-next-line no-console
        console.warn('[AssetDetailsView] synthesized metadata for fieldId not present in fields:', fid);
      } else {
        // Ensure label/style exist even for present entries
        out[fid].label = out[fid].label || cell.label || cell.name || fid;
        out[fid].style = out[fid].style || {};
      }
    });

    return out;
  }, [fields, layout]);

  /* ─────────────────────────────────────────────────────────────
     Build an answers map from the asset’s latest version snapshot
     (preferred) or fallback to metadata.responses
     ──────────────────────────────────────────────────────────── */
  const answers = useMemo(() => {
    if (!asset) return null;

    const vKey = (typeof asset.versionNumber === 'number') ? String(asset.versionNumber) : null;
    const versionNode = vKey && asset.versions && asset.versions[vKey];
    const base = normalizeResponsesMap(versionNode?.responses || asset?.metadata?.responses || {});

    // Inject attached asset ref so AttachFieldComponent shows the correct preview even if AUF’s attach field is empty
    if (normalizedFields && typeof normalizedFields === 'object') {
      const firstAttachUfi = Object.values(normalizedFields)
        .filter(m => m?.type === 'attach' || m?.type === 'image')
        .map(m => m?.ufi)
        .find(Boolean) || null;

      // Debug which UFI we chose
      // eslint-disable-next-line no-console
      console.log('[AssetDetailsView] attach UFI chosen:', firstAttachUfi);

      if (firstAttachUfi) {
        base[firstAttachUfi] = {
          assetId: asset.assetId || asset.id,
          ...(typeof asset.versionNumber === 'number' ? { versionNumber: asset.versionNumber } : {}),
        };
      }
    }

    // eslint-disable-next-line no-console
    console.log('[AssetDetailsView] normalized answers snapshot:', base);
    return base;
  }, [asset, normalizedFields]);


  /* ─────────────────────────────────────────────────────────────
     CTA label logic:
     - If we don't see a concrete version yet (null/0) OR no versions map,
       treat this as the first save → "Save to Library".
     - Otherwise we're updating an existing asset → "Save & Update".
     ───────────────────────────────────────────────────────────── */
  const isFirstSave = useMemo(() => {
    const v = asset?.versionNumber;
    const noVersionMap = !asset?.versions || Object.keys(asset.versions || {}).length === 0;
    return v == null || v === 0 || noVersionMap;
  }, [asset?.versionNumber, asset?.versions]);
  const submitCtaLabel = isFirstSave ? 'Save to Library' : 'Save & Update';

  // Permissions: allow “Edit” when the user has either upload or AUF edit ability
  const canEdit = Boolean(hasPermission?.('uploadAsset') || hasPermission?.('editUploadAssetForm'));

  /* ─────────────────────────────────────────────────────────────
     Navbar integration: external Edit/Done toggle
     - listen for "assetdetails:set-edit" → { detail: { value: boolean } }
     - broadcast "assetdetails:status" whenever isEditing changes
     - include basic context so the navbar can decide visibility
     ──────────────────────────────────────────────────────────── */
  useEffect(() => {
    const onSetEdit = (e) => {
      try {
        const next = !!e?.detail?.value;
        // eslint-disable-next-line no-console
        console.log('[AssetDetailsView] received assetdetails:set-edit →', next);
        setIsEditing(next);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[AssetDetailsView] set-edit handler failed:', err?.message || err);
      }
    };
    window.addEventListener('assetdetails:set-edit', onSetEdit);
    // Announce initial status so navbar can render the proper control
    try {
      window.dispatchEvent(new CustomEvent('assetdetails:status', {
        detail: {
          route: 'asset-details',
          canEdit,
          editing: false,
          assetId: assetId || null,
        },
      }));
    } catch (_) {}
    return () => {
      window.removeEventListener('assetdetails:set-edit', onSetEdit);
    };
  }, [assetId, canEdit]);

  useEffect(() => {
    // Broadcast whenever local edit state changes
    try {
      window.dispatchEvent(new CustomEvent('assetdetails:status', {
        detail: {
          route: 'asset-details',
          canEdit,
          editing: isEditing,
          assetId: assetId || null,
        },
      }));
    } catch (_) {}
  }, [isEditing, canEdit, assetId]);

  if (loading) return null;

  if (errorMsg) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{errorMsg}</Alert>
      </Box>
    );
  }

  // No AUF – light summary; Navbar is handled globally by the app shell.
  if (!aufId || !normalizedFields || !layout) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          {asset?.name || 'Asset'}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Asset Type: {asset?.assetType ?? '—'} · Version: {typeof asset?.versionNumber === 'number' ? asset.versionNumber : '—'} ·
          {' '}Dimensions: {asset?.dimensions?.width ?? '—'}×{asset?.dimensions?.height ?? '—'}
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Typography variant="body2">
          This organization has not published an Asset Upload Form. Metadata details are unavailable.
        </Typography>
      </Box>
    );
  }

  /* ─────────────────────────────────────────────────────────────
     SubmissionBridge: read handleSubmit only when providers exist
     ──────────────────────────────────────────────────────────── */
  function SubmissionBridge({ children }) {
    const ctx = useFormSubmissions() || {};
    return children(ctx);
  }

  /* ─────────────────────────────────────────────────────────────
     Inner AUF runtime (Navbar is global; no header here)
     NOTE: DO NOT wrap with a new FormProvider — use the outer one from routes.
     ──────────────────────────────────────────────────────────── */
  return (
    <>
      <FormThemeProvider initialTheme={theme || {}}>
      <FieldMetadataProvider initialFieldsMetadata={normalizedFields || {}}>
        <FormLayoutProvider initialLayoutConfig={layout || {}}>
          {/* Required for FormActionsProvider → useFieldEdit() */}
          <FieldEditProvider>
            {/* Base dropdown data */}
            <MultiAnswerFieldProvider initialDropdownOptions={options || {}}>
              {/* Answers go BEFORE deps/settings/actions like in PublishedFormPage */}
              <FormAnswersProvider persist={false}>
                {/* Dependent dropdowns subscribe to answers + metadata */}
                <DropdownDependenciesProvider>
                  {/* Mount ONCE; only toggle the submit handler by mode */}
                  <FormSubmissionSettingsProvider>
                    <FormActionsProvider>
                      <FormSubmissionsProvider>
                        {/* BodySurface stays mounted; submitHandler toggles */}
                        <SubmissionBridge>
                          {({ handleSubmit }) => (
                            <BodySurface
                              isEditing={isEditing}
                              showToast={showToast}
                              rawFields={normalizedFields}
                              rawLayout={layout}
                              submitHandler={
                                isEditing
                                  ? async () => {
                                      try {
                                        await handleSubmit(undefined, {
                                          submissionMode : "assetEdit",
                                          editingAssetId : asset?.id || assetId,
                                          targetVersion  : (
                                            typeof asset?.versionNumber === "number"
                                              ? asset.versionNumber
                                              : null
                                          ),
                                        });

                                        // optional: immediate re-fetch to reflect new snapshot
                                        try {
                                          const aRef = doc(db, "assets", String(asset?.id || assetId));
                                          const snap = await getDoc(aRef);
                                          if (snap.exists()) setAsset({ id: snap.id, ...snap.data() });
                                        } catch (rfErr) {
                                          console.warn("[AssetDetailsView] re-fetch after save failed:", rfErr?.message || rfErr);
                                        }
                                      } catch (e) {
                                        console.error("[AssetDetailsView] assetEdit submit failed", e?.message || e);
                                        showToast("Failed to update asset.", "error");
                                        throw e;
                                      }
                                    }
                                  : undefined
                              }
                              answers={answers}
                              submitLabel={submitCtaLabel}
                            />
                          )}
                        </SubmissionBridge>

                        {/* Single shared toast */}
                        <Snackbar
                          open={toastOpen}
                          autoHideDuration={3500}
                          onClose={() => setToastOpen(false)}
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                          sx={{
                            '& .MuiSnackbarContent-root': { maxWidth: 'none' },
                            '& .MuiPaper-root': { maxWidth: 'none' },
                          }}
                        >
                          <Alert
                            elevation={3}
                            variant="filled"
                            severity={toastSeverity}
                            onClose={() => setToastOpen(false)}
                            role="status"
                            sx={{
                              width: 'fit-content',
                              maxWidth: 'calc(100vw - 32px)',
                              display: 'inline-flex',
                              alignItems: 'center',
                              px: 1.5,
                              py: 0.75,
                              whiteSpace: 'nowrap',
                              '& .MuiAlert-message': {
                                display: 'inline-flex',
                                alignItems: 'center',
                                whiteSpace: 'inherit',
                                flex: '0 0 auto',
                              },
                              '& .MuiAlert-icon, & .MuiAlert-action': { flex: '0 0 auto' },
                            }}
                          >
                            {toastMsg}
                          </Alert>
                        </Snackbar>
                      </FormSubmissionsProvider>
                    </FormActionsProvider>
                  </FormSubmissionSettingsProvider>
                </DropdownDependenciesProvider>

              </FormAnswersProvider>
            </MultiAnswerFieldProvider>
          </FieldEditProvider>
        </FormLayoutProvider>
      </FieldMetadataProvider>
      </FormThemeProvider>
      {/* ───────── Versions drawer (history & quick actions) ───────── */}
      <VersionsDrawer
        open={versionsOpen}
        onClose={() => setVersionsOpen(false)}
        organizationId={organizationId}
        currentAsset={asset}
        onSelectVersion={handleSelectVersion}
        onAddNewVersion={handleAddNewVersion}
      />
    </>
  );
}

/* ──────────────────────────────────────────────────────────────
   AUF surface render (same pattern as PublishedFormPage) + Paper parity
   ────────────────────────────────────────────────────────────── */
function BodySurface({ isEditing, showToast, rawFields, rawLayout, submitHandler, answers, submitLabel }) {
  const { layoutConfig } = useFormLayout();
  const { fieldsMetadata } = useFieldMetadata();
  const { muiTheme, selectedTheme } = useFormTheme();
  const [submitting, setSubmitting] = React.useState(false);  // ← existing

  // NEW: ref mirror so guards work even across batched state updates
  const submittingRef = React.useRef(false);
  React.useEffect(() => { submittingRef.current = submitting; }, [submitting]);

  // Fallback to decompressed AUF blobs until providers finish loading
  const effectiveFields =
    (fieldsMetadata && Object.keys(fieldsMetadata).length > 0) ? fieldsMetadata : (rawFields || {});
  const effectiveLayout =
    (layoutConfig && Object.keys(layoutConfig).length > 0) ? layoutConfig : (rawLayout || {});

  // Gate hydrator until context actually exposes UFIs (prevents “unknown field” warnings)
  const hasContextUfis = useMemo(
    () => Object.values(fieldsMetadata || {}).some(m => !!m?.ufi),
    [fieldsMetadata]
  );

  // Debug snapshot for parity
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log('[AssetDetailsView] BodySurface snapshot:', {
      isEditing,
      layoutHasSubmit: !!(effectiveLayout?.submitRow && effectiveLayout.submitRow.length > 0),
      fieldsCount: effectiveFields ? Object.keys(effectiveFields).length : 0,
      selectedTheme,
      hasContextUfis,
    });
  }, [isEditing, effectiveLayout, effectiveFields, selectedTheme, hasContextUfis]);

  const hasSubmit = Boolean(effectiveLayout?.submitRow && effectiveLayout.submitRow.length > 0);


  /* ─────────────────────────────────────────────────────────────
     Unified submit runner:
     - Used by the inline "Save" CTA button below
     - Also invoked when dialog dispatches 'assetdetails:request-submit'
     - Always emits 'assetdetails:submit-status' with { ok: true|false }
     - CHANGE: flip "submitting" FIRST and emit 'assetdetails:request-submit'
       to prevent re-entrant submits from our own listener.
     ───────────────────────────────────────────────────────────── */
  const doSubmit = React.useCallback(async () => {
    try {
      if (typeof submitHandler !== 'function') {
        // eslint-disable-next-line no-console
        console.warn('[AssetDetailsView] submitHandler is not available in this mode.');
        return;
      }

      // IMPORTANT: set submitting first so the listener ignores our own request event
      setSubmitting(true);
      submittingRef.current = true; // keep the guard hot during this tick

      // Freeze previews across the page (AttachField listens and pauses swapping)
      try { window.dispatchEvent(new CustomEvent('assetdetails:request-submit')); } catch {}

      await submitHandler();  // your existing pipeline

      try {
        window.dispatchEvent(new CustomEvent('assetdetails:submit-status', { detail: { ok: true } }));
      } catch (_) {}
      showToast('Submitted successfully.', 'success');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[AssetDetailsView] Submit failed:', e);
      try {
        window.dispatchEvent(new CustomEvent('assetdetails:submit-status', { detail: { ok: false } }));
      } catch (_) {}
      showToast('Submission failed. Please try again.', 'error');
    } finally {
      setSubmitting(false);
      submittingRef.current = false;
    }
  }, [submitHandler, showToast]);

  /* Listen for dialog's "Save" (top-right)
     CHANGE: double-guard (state + ref) to avoid re-entry while we’re already submitting */
  React.useEffect(() => {
    const onReq = () => {
      if (!isEditing) return;   // ignore in view mode
      if (submitting || submittingRef.current) return; // guards against re-entry
      void doSubmit();
    };
    window.addEventListener('assetdetails:request-submit', onReq);
    return () => window.removeEventListener('assetdetails:request-submit', onReq);
  }, [isEditing, submitting, doSubmit]);

  // Common content builder
  const Content = (
    <>
      {/* Hydrate only when *not editing* (prevents snap-back after submit) */}
      {!isEditing && hasContextUfis && answers && (
        <AnswersHydrator answers={answers} />
      )}

      <PreviewFields
        fieldsMetadata={effectiveFields}
        layoutConfig={effectiveLayout}
        viewOnly={!isEditing}                 // ← NEW: tell FieldRenderer we’re in read-only mode
        // keep default: includeSubmitRow = false
      />

      {isEditing && hasSubmit && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
          <SubmitButtonComponent
            field={effectiveLayout.submitRow[0]}
            label={submitLabel} 
            loading={submitting} 
            loadingLabel="Saving" 
            onClick={() => {
              // inline guard matches the listener behavior
              if (!submitting && !submittingRef.current) void doSubmit();
            }}
          />
        </Box>
      )}
    </>
  );

  // Paper parity (same as PublishedFormPage)
  if (selectedTheme === 'Paper') {
    return (
      <Box
        sx={{
          p: 2,
          minHeight: '100vh',          // ← ensure the background fills the viewport
          boxSizing: 'border-box',
          backgroundColor: muiTheme?.palette?.background?.default,
          color: muiTheme?.palette?.text?.primary,
         // ...(isEditing ? {} : { pointerEvents: 'none' }),
        }}
      >
        <Paper elevation={0} sx={{ p: 2 }}>
          {Content}
        </Paper>
      </Box>
    );
  }

  // Full-page variant
  return (
    <Box
      sx={{
        p: 2,
        minHeight: '100vh',            // ← same here
        boxSizing: 'border-box',
        backgroundColor: muiTheme?.palette?.background?.default,
        color: muiTheme?.palette?.text?.primary,
       // ...(isEditing ? {} : { pointerEvents: 'none' }),
      }}
    >
      {Content}
    </Box>
  );
}

